'use client';

import { useAnalytics } from '@repo/analytics/analytics-provider';
import { useHostType } from '@repo/host/hooks/use-host-info';
import { decodeSharedChecklist, type SharedChecklistPayload } from '@repo/map-user-data/share';
import { SHARE_STATE_TOKEN_QUERY_PARAM } from '@repo/map-user-data/share-state';
import { useAppDispatch, useAppSelector } from '@repo/store/maps/hooks';
import { setSharedChecklistPayload, setSidebarView } from '@repo/store/maps/slices/clientContext';
import { selectExternalMapId, selectMapName, selectTitleName } from '@repo/store/maps/slices/mapData';
import { useEffect } from 'react';
import { getShareState } from '../../utils/share-state-client';
const SHARED_CHECKLIST_PARAM = 'cl';
let lastProcessedEncoded: string | null = null;
let lastProcessedShareStateKey: string | null = null;
export function useSharedChecklistUrlInitializer() {
  const dispatch = useAppDispatch();
  const analytics = useAnalytics();
  const hostType = useHostType();
  const externalMapId = useAppSelector(selectExternalMapId);
  const mapName = useAppSelector(selectMapName);
  const titleName = useAppSelector(selectTitleName);
  useEffect(() => {
    if (typeof window === 'undefined') return;
    const url = new URL(window.location.href);
    const openSharedChecklist = (payload: SharedChecklistPayload) => {
      dispatch(setSharedChecklistPayload(payload));
      dispatch(setSidebarView('sharedChecklist'));
      if (analytics && hostType) {
        analytics.event('game_map_shared_checklist_open', {
          mapId: externalMapId ?? undefined,
          mapName,
          titleName,
          pinCount: payload.pinIds.length,
          platform: hostType
        });
      }
    };
    const shareStateToken = url.searchParams.get(SHARE_STATE_TOKEN_QUERY_PARAM);
    if (shareStateToken) {
      if (!externalMapId) return;
      const shareStateKey = `${externalMapId}:${shareStateToken}`;
      if (shareStateKey === lastProcessedShareStateKey) return;
      lastProcessedShareStateKey = shareStateKey;
      void (async () => {
        try {
          const shareState = await getShareState(shareStateToken);
          if (shareState.kind !== 'sharedChecklist' || shareState.mapExternalId !== externalMapId) return;
          openSharedChecklist({
            title: shareState.title,
            description: shareState.description,
            pinIds: shareState.pinIds
          });
        } catch (error) {
          console.error('Failed to load shared checklist share state:', {
            error,
            mapExternalId: externalMapId,
            token: shareStateToken
          });
        }
      })();
      return;
    }
    const encoded = url.searchParams.get(SHARED_CHECKLIST_PARAM);
    if (!encoded) return;
    if (encoded === lastProcessedEncoded) return;
    lastProcessedEncoded = encoded;
    void (async () => {
      const payload_0 = await decodeSharedChecklist(encoded);
      if (!payload_0) return;
      openSharedChecklist(payload_0);
    })();
  }, [dispatch, analytics, hostType, externalMapId, mapName, titleName]);
}